import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { OpenDeal } from "../api";
import { stageVariantFor } from "../constants";
import { fmtCents, fmtDate } from "../format";

type SortKey =
  | "name"
  | "stage_name"
  | "owner_display_name"
  | "amount_cents"
  | "close_date";

export interface OpenDealsFilter {
  ownerNames: string[];
  stages: string[];
  closeFrom: string | null;
  closeTo: string | null;
}

/**
 * Open-deals table. Filters (owner / stage / close-date range) live in the
 * parent (OpenDealsPage) so they sit ABOVE the table in the page layout;
 * this component only applies them + handles sort + search.
 */
export function OpenDealsTable({
  deals,
  filter,
  onSelect,
}: {
  deals: OpenDeal[];
  filter: OpenDealsFilter;
  onSelect: (deal: OpenDeal) => void;
}) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("amount_cents");
  const [sortDir, setSortDir] = useState<-1 | 1>(-1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let data = deals;
    if (q) {
      data = data.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.owner_display_name ?? "").toLowerCase().includes(q),
      );
    }
    if (filter.ownerNames.length > 0) {
      const ownerSet = new Set(filter.ownerNames);
      data = data.filter(
        (d) => d.owner_display_name && ownerSet.has(d.owner_display_name),
      );
    }
    if (filter.stages.length > 0) {
      const stageSet = new Set(filter.stages);
      data = data.filter((d) => stageSet.has(d.stage_name));
    }
    if (filter.closeFrom) {
      const from = new Date(filter.closeFrom).getTime();
      data = data.filter(
        (d) => d.close_date && new Date(d.close_date).getTime() >= from,
      );
    }
    if (filter.closeTo) {
      const to = new Date(filter.closeTo).getTime();
      data = data.filter(
        (d) => d.close_date && new Date(d.close_date).getTime() <= to,
      );
    }
    data = [...data].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortKey];
      const bv = (b as unknown as Record<string, unknown>)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir * (av - bv);
      }
      return sortDir * String(av).localeCompare(String(bv));
    });
    return data;
  }, [deals, query, sortKey, sortDir, filter]);

  function setSort(k: SortKey) {
    if (k === sortKey) {
      setSortDir(sortDir === 1 ? -1 : 1);
    } else {
      setSortKey(k);
      setSortDir(k === "amount_cents" ? -1 : 1);
    }
  }

  const arrow = sortDir === 1 ? " ↑" : " ↓";

  const totalCents = filtered.reduce((a, d) => a + (d.amount_cents ?? 0), 0);
  const withAmount = filtered.filter((d) => d.amount_cents != null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Input
          className="w-64"
          placeholder="Search deals or owner…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex gap-3 font-mono text-xs text-muted-foreground">
          <span>{filtered.length} deals</span>
          <span>·</span>
          <span>Total {fmtCents(totalCents)}</span>
          <span>·</span>
          <span>
            Avg{" "}
            {withAmount.length
              ? fmtCents(Math.round(totalCents / withAmount.length))
              : "—"}
          </span>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => setSort("name")}
              >
                Deal{sortKey === "name" ? arrow : ""}
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => setSort("stage_name")}
              >
                Stage{sortKey === "stage_name" ? arrow : ""}
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => setSort("owner_display_name")}
              >
                Owner{sortKey === "owner_display_name" ? arrow : ""}
              </TableHead>
              <TableHead
                className="cursor-pointer select-none text-right"
                onClick={() => setSort("amount_cents")}
              >
                Amount{sortKey === "amount_cents" ? arrow : ""}
              </TableHead>
              <TableHead
                className="cursor-pointer select-none"
                onClick={() => setSort("close_date")}
              >
                Close{sortKey === "close_date" ? arrow : ""}
              </TableHead>
              <TableHead className="text-right">Prob</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => {
              const closeDate = d.close_date ? new Date(d.close_date) : null;
              const overdue = closeDate ? closeDate < today : false;
              return (
                <TableRow
                  key={d.hubspot_deal_id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => onSelect(d)}
                >
                  <TableCell className="font-medium">
                    {d.name}
                    {d.is_strategic && (
                      <span className="ml-2 rounded bg-power/10 px-1.5 py-0.5 text-[10px] font-medium text-power">
                        Strategic
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={stageVariantFor(d.stage_name)}>
                      {d.stage_name}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {d.owner_display_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {d.amount_cents != null ? (
                      fmtCents(d.amount_cents)
                    ) : (
                      <span className="text-muted-foreground italic">No amount</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={`font-mono text-xs ${
                      overdue ? "text-danger" : "text-muted-foreground"
                    }`}
                  >
                    {fmtDate(d.close_date ?? null)}
                    {overdue ? " ⚠" : ""}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {d.commit_probability != null
                      ? `${Math.round(d.commit_probability * 100)}%`
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  No deals match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
